import { Avatar, Box, ListItemAvatar, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { IconHeadset } from '@shared/assets/images/svgs';
import React from 'react';
import { messageTimeCalculation } from './utils';
import type { TestingConversationViewMessage } from './useTestingConversationMessages';
import { ToolCallCard } from '@widgets/ToolCallCard/ToolCallCard';

interface TestingConversationMessageProps {
  messageItem: TestingConversationViewMessage;
  actorName?: string;
  isStacked?: boolean;
  timeSincePreviousMessage?: number;
}

export const TestingConversationMessage: React.FC<TestingConversationMessageProps> = ({
  messageItem,
  actorName,
  isStacked,
  timeSincePreviousMessage,
}: TestingConversationMessageProps) => {
  const { actor } = messageItem;
  const isAgent: boolean = actor === 'agent';
  const isToolCall = messageItem.type === 'tool_call' && Boolean(messageItem.toolCall);

  return (
    <Box
      display="flex"
      alignItems="flex-end"
      flexDirection={isAgent ? 'row' : 'row-reverse'}
      sx={{
        mt: isStacked ? '3px' : '16px',
        mb: '2px',
        ml: isToolCall || isAgent ? 0 : '52px',
        ...(isToolCall ? { width: '100%', justifyContent: 'center' } : {}),
      }}>
      {isAgent && !isStacked && !isToolCall && (
        <ListItemAvatar sx={{ minWidth: 'unset', mr: '10px' }}>
          <Avatar
            sx={(theme) => ({
              width: 32,
              height: 32,
              backgroundColor: theme.palette.primary.light,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            })}>
            <IconHeadset style={{ fontSize: 16 }} />
          </Avatar>
        </ListItemAvatar>
      )}
      {isAgent && isStacked && !isToolCall && <Box sx={{ width: 32, mr: '10px', flexShrink: 0 }} />}

      <Box
        display="flex"
        flexDirection="column"
        alignItems={isToolCall ? 'center' : isAgent ? 'flex-start' : 'flex-end'}
        sx={{ maxWidth: isToolCall ? '100%' : '75%', width: isToolCall ? '100%' : undefined }}>
        {!isStacked && !isToolCall && (
          <Typography
            sx={(theme) => ({
              fontWeight: 600,
              fontSize: '12px',
              color: theme.palette.extendedGrey.dark,
              mb: '4px',
            })}>
            {actorName || actor}
          </Typography>
        )}
        <Box
          sx={(theme) =>
            isToolCall
              ? { width: '100%' }
              : {
                  p: '10px 14px',
                  borderRadius: isAgent ? '4px 14px 14px 14px' : '14px 4px 14px 14px',
                  backgroundColor: isAgent
                    ? theme.palette.surface.raised
                    : theme.palette.primary.main,
                  color: isAgent ? theme.palette.text.primary : theme.palette.common.white,
                  fontSize: '14px',
                  lineHeight: 1.55,
                  wordBreak: 'break-word',
                  boxShadow: isAgent
                    ? `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`
                    : `0 1px 3px ${alpha(theme.palette.primary.main, 0.2)}`,
                }
          }>
          {messageItem.type === 'tool_call' && messageItem.toolCall ? (
            <ToolCallCard toolCall={messageItem.toolCall} />
          ) : (
            messageItem.message
          )}
        </Box>
        <Stack direction="row" alignItems="center" sx={{ mt: '4px' }}>
          <Typography sx={(theme) => ({ fontSize: '11px', color: theme.palette.text.secondary })}>
            {messageTimeCalculation(messageItem.createdAt)}
          </Typography>
          {timeSincePreviousMessage !== undefined && (
            <Typography
              sx={(theme) => ({
                fontSize: '11px',
                color: theme.palette.text.secondary,
                ml: '6px',
              })}>
              ( +{timeSincePreviousMessage} sec )
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
